import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  auth,
  getMenu,
  makeOrder,
  makeOrderAsCashier,
  setItemAvailability,
  getAppSettings,
} from '../api';
import menuUtils from '../utils/menu-utils';
import { useMenu } from '../contexts/menu-with-quantities-ctx';
import { useProduct } from '../contexts/product-ctx';
import { useAppSettings } from '../contexts/app-settings-ctx';
import { checkIfAllDatesAreExpired } from '../utils/menu2-utils';

export function useMenuLogic() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isAnonymousPreview =
    location.pathname === '/menu' && searchParams.get('anonymousPreview') === '1';

  const effectiveUser = isAnonymousPreview ? null : user;

  const isAdmin = effectiveUser?.displayName === 'admin';
  const isOperator = effectiveUser?.displayName === 'operator';
  const isPromoter = effectiveUser?.displayName === 'promoter';
  const isCashier = effectiveUser?.displayName === 'cashier';
  const isCustomer = !effectiveUser;

  const [render, rerender] = useState(false);
  const { menu, setMenu } = useMenu();
  const { appSettings, setAppSettings, reset } = useAppSettings();
  const { product } = useProduct();

  const [selectedDate, setSelectedDate] = useState();
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [progress, setProgress] = useState();
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [showModalSelectDate, setShowModalSelectDate] = useState(false);
  const [showModalPriorityAccess, setShowModalPriorityAccess] = useState(false);
  const [showModalAppIsClosed, setShowModalAppIsClosed] = useState(false);
  const [showModalBuyCustomer, setShowModalBuyCustomer] = useState(false);
  const [showModalConfirmOrderCustomer, setShowModalConfirmOrderCustomer] = useState(false);
  const [showModalConfirmOrderCashier, setShowModalConfirmOrderCashier] = useState(false);
  const [showModalResultCashier, setShowModalResultCashier] = useState(false);
  const [modalCashierText, setModalCashierText] = useState('');
  const [cashierOrderId, setCashierOrderId] = useState('');
  const [voucher, setVoucher] = useState();

  // --- FUNZIONI DI LOGICA ESTRATTE ---
  const _updateProductQuantities = (arrayOrInt, factor) => {
    if (!Array.isArray(arrayOrInt)) {
      return parseInt(arrayOrInt) + factor;
    }
    return arrayOrInt.map((element) => {
      if (new Date(element.date).getTime() === new Date(selectedDate).getTime()) {
        return { ...element, qty: parseInt(element.qty) + factor };
      }
      return element;
    });
  };

  const _updateProductsOfComboQuantities = (prevMenu, id, factor) => {
    const combo = prevMenu.combos.find((combo) => combo.id === id);
    const pIds = combo.products.map((p) => p.id);
    prevMenu['products'].forEach((p) => {
      if (pIds.includes(p.id)) {
        p.tempQuantities = _updateProductQuantities(p.tempQuantities, +factor);
      }
    });
  };

  const increment = (id, key) => {
    setMenu((prev) => {
      if (key === 'products') {
        prev[key] = prev[key].map((_item) => {
          return _item.id === id
            ? {
                ..._item,
                quantity: _item.quantity + 1,
                tempQuantities: _updateProductQuantities(_item.tempQuantities, -1),
              }
            : _item;
        });
      }
      if (key === 'combos') {
        prev[key] = prev[key].map((_item) => {
          return _item.id === id
            ? {
                ..._item,
                quantity: _item.quantity + 1,
              }
            : _item;
        });
        _updateProductsOfComboQuantities(prev, id, -1);
      }
      return { ...prev };
    });
  };

  const decrement = (id, key) => {
    setMenu((prev) => {
      if (key === 'products') {
        prev[key] = prev[key].map((_item) => {
          return _item.id === id
            ? {
                ..._item,
                quantity: _item.quantity - 1,
                tempQuantities: _updateProductQuantities(_item.tempQuantities, +1),
              }
            : _item;
        });
      }
      if (key === 'combos') {
        prev[key] = prev[key].map((_item) => {
          return _item.id === id
            ? {
                ..._item,
                quantity: _item.quantity - 1,
              }
            : _item;
        });
        _updateProductsOfComboQuantities(prev, id, +1);
      }
      return { ...prev };
    });
  };

  const setUnavailable = async (id, isProduct) => {
    setIsLoadingMenu(true);
    const res = await setItemAvailability(id, isProduct, false);
    setIsLoadingMenu(false);
    if (res.success) {
      menuUtils.setAvailability(menu, id, false);
      setMenu(menu);
      rerender(!render);
    }
  };

  const setAvailable = async (id, isProduct) => {
    setIsLoadingMenu(true);
    const res = await setItemAvailability(id, isProduct, true);
    setIsLoadingMenu(false);
    if (res.success) {
      menuUtils.setAvailability(menu, id, true);
      setMenu(menu);
      rerender(!render);
    }
  };

  // Gestione acquisto utente
  const handleCustomerOrderConfirm = async () => {
    setIsLoadingMenu(true);
    const { products, combos } = menuUtils.minimize(menu);
    const _checkoutUrl = await makeOrder(products, combos, selectedDate, voucher);
    setCheckoutUrl(_checkoutUrl);
    setShowModalConfirmOrderCustomer(false);
    setIsLoadingMenu(false);
    setShowModalBuyCustomer(true);
    // Azzera le quantità dopo ordine
    menuUtils.clearQuantities(menu);
    setMenu(menu);
  };

  // Gestione ordine cassa
  const handleCashierOrderConfirm = async (paymentType) => {
    // console.log('paymentType', paymentType);
    setIsLoadingMenu(true);
    setShowModalConfirmOrderCashier(false);
    const { products, combos } = menuUtils.minimize(menu);

    const clientTotal = menuUtils.total(menu); // NON tiene conto dei voucher, la cassa non supporta i voucher
    const result = await makeOrderAsCashier(products, combos, selectedDate, paymentType, clientTotal);
    
    setIsLoadingMenu(false);
    // Mostra risultato
    if (result.success) {
      setShowModalResultCashier(true);
      setModalCashierText('Ordine inserito correttamente.');
      setCashierOrderId(result.orderId);
      await reloadMenu();
    } else {
      setShowModalResultCashier(true);
      setModalCashierText('Errore ' + result.error + '. Contattare il supporto.');
    }
    // Azzera le quantità dopo ordine
    // menuUtils.clearQuantities(menu);
    // setMenu(menu);
  };

  // Funzione riutilizzabile per ricaricare il menu da remoto
  const reloadMenu = async () => {
    setIsLoadingMenu(true);
    const m = await getMenu((_progress) => {
      setProgress(_progress);
    });
    menuUtils.clearQuantities(m);
    menuUtils.orderByPosition(m);
    setMenu(m);
    setIsLoadingMenu(false);
  };

  // --- FINE FUNZIONI DI LOGICA ---

  // Caricamento settings
  useEffect(() => {
    const _getAppSettings = async () => {
      setIsLoadingSettings(true);
      const settings = await getAppSettings();
      setAppSettings(settings);
      setIsLoadingSettings(false);

      // Se skipDate non fare niente
      if (settings.skipDate) return;

      // Se tutte le date sono closed
      // oppure
      // Se tutte le date sono passate da almeno 4 ore dal momento corrente
      const isClosed =
        settings.eventDates.every((item) => item.status === 'closed') ||
        checkIfAllDatesAreExpired(
          settings.eventDates.filter((item) => item.status !== 'closed').map((item) => item.date),
        );

      if (isClosed) {
        setShowModalAppIsClosed(true);
        return;
      }

      setShowModalSelectDate(true);
    };
    _getAppSettings();
  }, []);

  // Caricamento menu
  useEffect(() => {
    if (menuUtils.isEmpty(menu)) {
      reloadMenu();
    }
  }, []);

  // Resetta le quantità se cambi data
  useEffect(() => {
    menuUtils.clearQuantities(menu);
  }, [selectedDate]);

  // AGGIORNA il campo 'qtyForDate' DI PRODUCTS E COMBO
  useEffect(() => {
    if (menuUtils.isEmpty(menu)) return;
    // CASO SKIP DATE
    if (appSettings.skipDate) {
      menu.products.forEach((product) => {
        product.qtyForDate = product.tempQuantities; // absolute
      });
      // TODO QUANTITIES FOR SKIP DATECOMBOS
      // ...
      rerender(!render);
      return;
    }
    // CASO NO SKIP DATE
    if (!selectedDate) return;

    menu.products.forEach((product) => {
      const found = product.tempQuantities.find((item) => {
        const dateA = new Date(item.date);
        const dateB = new Date(selectedDate);
        return dateA.getTime() === dateB.getTime();
      });
      product.qtyForDate = found ? found.qty : 0;
    });
    menu.combos.forEach((combo) => {
      const pIds = combo.products.map((p) => p.id);
      const minQtyForDate = pIds.reduce((minQty, productId) => {
        const product = menu.products.find((p) => p.id === productId);
        if (product && product.qtyForDate < minQty) {
          return product.qtyForDate;
        }
        return minQty;
      }, Infinity);
      combo.qtyForDate = minQtyForDate;
    });
    rerender(!render);
    // Aggiunto skipDate per i casi in cui skipDate = 1, quindi senza date
  }, [selectedDate, JSON.stringify(menu), appSettings.skipDate]);

  return {
    user,
    isAdmin,
    isOperator,
    isPromoter,
    isCustomer,
    isCashier,
    menu,
    setMenu,
    appSettings,
    setAppSettings,
    reset,
    product,
    selectedDate,
    setSelectedDate,
    isLoadingMenu,
    setIsLoadingMenu,
    isLoadingSettings,
    setIsLoadingSettings,
    progress,
    setProgress,
    checkoutUrl,
    setCheckoutUrl,
    showModalSelectDate,
    setShowModalSelectDate,
    showModalPriorityAccess,
    setShowModalPriorityAccess,
    showModalAppIsClosed,
    setShowModalAppIsClosed,
    showModalBuyCustomer,
    setShowModalBuyCustomer,
    showModalConfirmOrderCustomer,
    setShowModalConfirmOrderCustomer,
    showModalConfirmOrderCashier,
    setShowModalConfirmOrderCashier,
    showModalResultCashier,
    setShowModalResultCashier,
    modalCashierText,
    setModalCashierText,
    cashierOrderId,
    setCashierOrderId,
    voucher,
    setVoucher,
    render,
    rerender,
    navigate,
    increment,
    decrement,
    setAvailable,
    setUnavailable,
    handleCustomerOrderConfirm,
    handleCashierOrderConfirm,
  };
} 